import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Building2,
  Calendar,
  ClipboardCopy,
  HardHat,
  MapPin,
  Plus,
  Save,
  Search,
  Send,
  Users,
  Wrench,
} from "lucide-react";
import type { DailyEntryDay, DailyEntryOptions } from "@crewledger/shared/schemas";
import { EmptyState, InfoBanner, ListTableSkeleton, PageHeader, useToast } from "@/components/design";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { validateGeofence } from "@crewledger/shared/geofence";
import type { SubmitDailyEntryInput } from "@crewledger/shared/schemas";
import { captureGeolocation, deviceInfoSummary } from "@/lib/geolocation";
import { BETA_EMPTY_STATES } from "@/lib/beta-empty-states";
import { GeofenceSubmitModal } from "./components/geofence-submit-modal";
import { cn } from "@/lib/utils";
import { MICROCOPY } from "@/lib/microcopy";
import { formatHours } from "@/features/dashboard/utils";
import { useAuth } from "@/features/auth/auth-provider";
import { fetchSites } from "@/features/sites/api";
import {
  copyPreviousDailyEntry,
  fetchDailyEntryDay,
  fetchDailyEntryOptions,
  saveDailyEntryDraft,
  submitDailyEntry,
} from "./api";
import { AddCrewModal } from "./add-crew-modal";
import { AddWorkerModal } from "./add-worker-modal";
import {
  ApplyEquipmentModal,
  type ApplyEquipmentSelection,
  type ApplyEquipmentTarget,
} from "./apply-equipment-modal";
import { DailyEntrySummaryPanel } from "./daily-entry-summary-panel";
import { DailyEntryTable } from "./daily-entry-table";
import { useTimeEntryPermissions } from "./use-time-entry-permissions";
import {
  createEmptyRow,
  summarizeRows,
  todayIso,
  type DailyEntryUiRow,
} from "./utils";

type SiteChoice = { id: string; name: string; code: string };

function applyEquipmentToRows(
  rows: DailyEntryUiRow[],
  targetWorkerIds: Set<string>,
  selection: ApplyEquipmentSelection,
): DailyEntryUiRow[] {
  return rows.map((row) => {
    if (!targetWorkerIds.has(row.workerId)) return row;

    const usageHours = selection.hoursMode === "match_labor" ? row.hours : selection.customHours;
    const equipmentLines = selection.equipmentIds.map((equipmentId) => ({
      equipmentId,
      usageHours,
    }));

    if (selection.mergeMode === "append") {
      const existingIds = new Set(row.equipment.map((e) => e.equipmentId));
      return {
        ...row,
        equipment: [
          ...row.equipment,
          ...equipmentLines.filter((line) => !existingIds.has(line.equipmentId)),
        ],
      };
    }

    return { ...row, equipment: equipmentLines };
  });
}

function rowsFromDay(day: DailyEntryDay): DailyEntryUiRow[] {
  return day.rows.map((row) => ({
    clientId: row.id ?? crypto.randomUUID(),
    id: row.id,
    workerId: row.workerId,
    employeeId: row.employeeId,
    workerName: row.workerName,
    crew: row.crew,
    hours: row.hours,
    jobCode: row.jobCode ?? "",
    costCode: row.costCode ?? "",
    notes: row.notes ?? "",
    flags: row.flags,
    equipment: row.equipment,
    geofenceStatus: row.geofenceStatus ?? null,
  }));
}

export function DailyEntryPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { canRead, canWrite, canSubmit, canViewPayroll, canViewEquipmentCosts } = useTimeEntryPermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const [siteId, setSiteId] = useState(searchParams.get("siteId") ?? "");
  const [date, setDate] = useState(searchParams.get("date") ?? todayIso());
  const [siteChoices, setSiteChoices] = useState<SiteChoice[]>([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [options, setOptions] = useState<DailyEntryOptions | null>(null);
  const [dayMeta, setDayMeta] = useState<DailyEntryDay | null>(null);
  const [rows, setRows] = useState<DailyEntryUiRow[]>([]);
  const [yesterdayRows, setYesterdayRows] = useState<DailyEntryUiRow[]>([]);
  const [activeCrew, setActiveCrew] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [crewModalOpen, setCrewModalOpen] = useState(false);
  const [workerModalOpen, setWorkerModalOpen] = useState(false);
  const [applyEquipmentModalOpen, setApplyEquipmentModalOpen] = useState(false);
  const [applyEquipmentTarget, setApplyEquipmentTarget] = useState<ApplyEquipmentTarget>("crew");
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [geofenceModalOpen, setGeofenceModalOpen] = useState(false);
  const [geofenceOverrideReason, setGeofenceOverrideReason] = useState("");
  const [pendingGeolocation, setPendingGeolocation] = useState<SubmitDailyEntryInput["geolocation"]>(null);
  const debouncedSearch = useDebouncedValue(search, 250);

  useEffect(() => {
    let cancelled = false;
    setSitesLoading(true);
    fetchSites({ pageSize: 100 })
      .then((res) => {
        if (cancelled) return;
        const active = res.data
          .filter((site) => site.status === "ACTIVE")
          .map((site) => ({ id: site.id, name: site.name, code: site.code }));
        setSiteChoices(active);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        toast({
          variant: "error",
          title: "Failed to load sites",
          description: err instanceof Error ? err.message : undefined,
        });
        setSiteChoices([]);
      })
      .finally(() => {
        if (!cancelled) setSitesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const load = useCallback(async () => {
    if (!siteId) {
      setLoading(false);
      setOptions(null);
      setDayMeta(null);
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const [optionsRes, dayRes] = await Promise.all([
        fetchDailyEntryOptions(siteId, date),
        fetchDailyEntryDay(siteId, date),
      ]);
      setOptions(optionsRes.data);
      setDayMeta(dayRes.data);
      setRows(rowsFromDay(dayRes.data));
      if (dayRes.data.previousDate) {
        const prev = await copyPreviousDailyEntry(siteId, date);
        setYesterdayRows(
          prev.data.map((row) => {
            const worker =
              optionsRes.data.workersById[row.workerId] ??
              optionsRes.data.workers.find((w) => w.id === row.workerId);
            return {
              ...row,
              clientId: crypto.randomUUID(),
              employeeId: worker?.employeeId ?? "",
              workerName: worker?.displayName ?? "",
              crew: worker?.crew ?? null,
              jobCode: row.jobCode ?? "",
              costCode: row.costCode ?? "",
              notes: row.notes ?? "",
            };
          }),
        );
      } else {
        setYesterdayRows([]);
      }
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to load daily entry",
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setLoading(false);
    }
  }, [siteId, date, toast]);

  useEffect(() => {
    if (!siteId && user?.siteIds?.length === 1) {
      setSiteId(user.siteIds[0]!);
    }
  }, [user, siteId]);

  useEffect(() => {
    if (siteId || siteChoices.length === 0) return;
    const urlSiteId = searchParams.get("siteId");
    if (urlSiteId) return;
    setSiteId(siteChoices[0]!.id);
  }, [siteChoices, siteId, searchParams]);

  const selectableSites = siteChoices.length > 0 ? siteChoices : (options?.sites ?? []);

  useEffect(() => {
    setSearchParams({ siteId, date }, { replace: true });
    void load();
  }, [siteId, date, load, setSearchParams]);

  const filteredRows = useMemo(() => {
    let next = rows;
    if (activeCrew !== "all") {
      const crew = options?.crews.find((c) => c.id === activeCrew);
      if (crew) next = next.filter((row) => crew.workerIds.includes(row.workerId));
    }
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.trim().toLowerCase();
      next = next.filter(
        (row) =>
          row.workerName.toLowerCase().includes(q) ||
          row.employeeId.toLowerCase().includes(q) ||
          row.jobCode?.toLowerCase().includes(q),
      );
    }
    return next;
  }, [rows, activeCrew, debouncedSearch, options?.crews]);

  const summary = useMemo(() => {
    if (!options) {
      return dayMeta?.summary ?? {
        workerCount: 0,
        totalHours: 0,
        regularHours: 0,
        overtimeHours: 0,
        doubleTimeHours: 0,
        equipmentHours: 0,
        estimatedGrossPay: null,
        equipmentCost: 0,
        exceptionCount: 0,
      };
    }
    return summarizeRows(rows, options, date, canViewPayroll);
  }, [rows, options, date, canViewPayroll, dayMeta?.summary]);

  const payload = useMemo(
    () => ({
      siteId,
      date,
      rows: rows.map((row) => ({
        id: row.id,
        workerId: row.workerId,
        hours: row.hours,
        jobCode: row.jobCode,
        costCode: row.costCode,
        notes: row.notes,
        flags: row.flags,
        equipment: row.equipment.filter((e) => e.equipmentId),
      })),
    }),
    [siteId, date, rows],
  );

  const handleSaveDraft = async () => {
    if (!canWrite || dayMeta?.isLocked) return;
    setSaving(true);
    try {
      const res = await saveDailyEntryDraft(payload);
      setDayMeta(res.data);
      setRows(rowsFromDay(res.data));
      toast({ variant: "success", title: "Draft saved" });
    } catch (err) {
      toast({
        variant: "error",
        title: "Save failed",
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (overrideReason?: string, geolocation?: SubmitDailyEntryInput["geolocation"]) => {
    if (!canSubmit || dayMeta?.isLocked) return;
    if (summary.exceptionCount > 0) {
      toast({
        variant: "error",
        title: "Fix exceptions before submitting",
        description: `${summary.exceptionCount} blocking issue(s) remain.`,
      });
      return;
    }

    const geofence = dayMeta?.geofence;
    let location = geolocation;
    if (geofence?.enabled && location === undefined) {
      location = await captureGeolocation();
    }

    if (geofence?.enabled) {
      const validation = validateGeofence(geofence, location, overrideReason);
      if (validation.blocked) {
        toast({
          variant: "error",
          title: "Geofence blocked",
          description:
            validation.status === "GPS_UNAVAILABLE"
              ? "GPS is required for this site in strict mode."
              : "You must be on site to submit in strict mode.",
        });
        return;
      }
      if (validation.requiresOverrideReason && !overrideReason?.trim()) {
        setPendingGeolocation(location ?? null);
        setGeofenceOverrideReason("");
        setGeofenceModalOpen(true);
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await submitDailyEntry({
        ...payload,
        geolocation: geofence?.enabled ? (location ?? null) : undefined,
        geofenceOverrideReason: overrideReason?.trim() || undefined,
        deviceInfo: geofence?.enabled ? deviceInfoSummary() : undefined,
      });
      setGeofenceModalOpen(false);
      if (res.data.geofenceWarnings.length > 0) {
        toast({
          variant: "warning",
          title: "Day submitted with geofence notes",
          description: res.data.geofenceWarnings.join(" · "),
        });
      } else {
        toast({ variant: "success", title: "Day submitted" });
      }
      await load();
    } catch (err) {
      toast({
        variant: "error",
        title: "Submit failed",
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyPreviousDay = async () => {
    try {
      const res = await copyPreviousDailyEntry(siteId, date);
      if (res.data.length === 0) {
        toast({ variant: "error", title: "No previous day to copy" });
        return;
      }
      const merged = new Map(rows.map((row) => [row.workerId, row]));
      for (const row of res.data) {
        const worker = options?.workers.find((w) => w.id === row.workerId);
        if (!worker) continue;
        merged.set(row.workerId, {
          ...row,
          clientId: merged.get(row.workerId)?.clientId ?? crypto.randomUUID(),
          id: merged.get(row.workerId)?.id,
          employeeId: worker.employeeId,
          workerName: worker.displayName,
          crew: worker.crew,
          jobCode: row.jobCode ?? "",
          costCode: row.costCode ?? "",
          notes: row.notes ?? "",
        });
      }
      setRows(Array.from(merged.values()));
      toast({ variant: "success", title: "Copied previous day" });
    } catch (err) {
      toast({
        variant: "error",
        title: "Copy failed",
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleAddWorker = (worker: DailyEntryOptions["workers"][number]) => {
    if (rows.some((row) => row.workerId === worker.id)) {
      toast({ variant: "error", title: "Worker already on the board" });
      return;
    }
    setRows((prev) => [...prev, createEmptyRow(worker)]);
    setOptions((current) =>
      current
        ? {
            ...current,
            workersById: { ...current.workersById, [worker.id]: worker },
            workers: current.workers.some((w) => w.id === worker.id)
              ? current.workers
              : [...current.workers, worker],
          }
        : current,
    );
  };

  const applyCrewToBoard = (crewId: string, defaultHours: number) => {
    const crew = options?.crews.find((c) => c.id === crewId);
    if (!crew) return;

    const additions: DailyEntryUiRow[] = [];
    let skipped = 0;
    let inactiveCount = 0;

    for (const member of crew.members) {
      if (rows.some((row) => row.workerId === member.workerId)) {
        skipped += 1;
        continue;
      }
      const worker =
        options?.workersById[member.workerId] ??
        options?.workers.find((w) => w.id === member.workerId);
      if (!worker) continue;
      if (member.workerStatus === "INACTIVE") inactiveCount += 1;

      const row = createEmptyRow(worker);
      row.hours = defaultHours;
      row.jobCode = member.defaultJobCode ?? "";
      row.costCode = member.defaultCostCode ?? "";
      if (member.defaultEquipmentId) {
        row.equipment = [
          {
            equipmentId: member.defaultEquipmentId,
            usageHours: defaultHours,
          },
        ];
      }
      additions.push(row);
    }

    if (additions.length === 0) {
      toast({ variant: "error", title: "All crew workers are already on the board" });
      return;
    }

    setRows((prev) => [...prev, ...additions]);
    toast({
      variant: inactiveCount > 0 ? "error" : "success",
      title: `Added ${additions.length} worker${additions.length === 1 ? "" : "s"}`,
      description:
        [
          skipped > 0 ? `${skipped} duplicate${skipped === 1 ? "" : "s"} skipped` : null,
          inactiveCount > 0 ? `${inactiveCount} inactive worker${inactiveCount === 1 ? "" : "s"}` : null,
        ]
          .filter(Boolean)
          .join(" · ") || undefined,
    });
  };

  const getActiveCrewWorkerIds = () => {
    if (activeCrew === "all") return null;
    const crew = options?.crews.find((c) => c.id === activeCrew);
    return new Set(crew?.members.map((m) => m.workerId) ?? []);
  };

  const handleApplyEquipmentToCrew = () => {
    if (activeCrew === "all") {
      toast({ variant: "error", title: "Select a crew tab first" });
      return;
    }
    setApplyEquipmentTarget("crew");
    setApplyEquipmentModalOpen(true);
  };

  const handleApplyEquipmentToSelected = () => {
    if (selectedRowIds.size === 0) {
      toast({ variant: "error", title: "Select at least one worker" });
      return;
    }
    setApplyEquipmentTarget("selected");
    setApplyEquipmentModalOpen(true);
  };

  const handleApplyEquipment = (selection: ApplyEquipmentSelection) => {
    let targetWorkerIds: Set<string>;
    if (applyEquipmentTarget === "crew") {
      const workerIds = getActiveCrewWorkerIds();
      if (!workerIds) return;
      targetWorkerIds = workerIds;
    } else {
      targetWorkerIds = new Set(
        rows.filter((row) => selectedRowIds.has(row.clientId)).map((row) => row.workerId),
      );
    }

    setRows((prev) => applyEquipmentToRows(prev, targetWorkerIds, selection));
    toast({
      variant: "success",
      title: "Equipment applied",
      description: `${selection.equipmentIds.length} item${selection.equipmentIds.length === 1 ? "" : "s"} assigned`,
    });
  };

  const handleCopyYesterdayEquipment = () => {
    if (yesterdayRows.length === 0) {
      toast({ variant: "error", title: "No previous day equipment to copy" });
      return;
    }

    const yesterdayByWorker = new Map(yesterdayRows.map((row) => [row.workerId, row.equipment]));
    setRows((prev) =>
      prev.map((row) => {
        const equipment = yesterdayByWorker.get(row.workerId);
        return equipment ? { ...row, equipment } : row;
      }),
    );
    toast({ variant: "success", title: "Copied yesterday's equipment assignments" });
  };

  const handleToggleRowSelected = (clientId: string) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const handleToggleAllRowsSelected = (selected: boolean) => {
    setSelectedRowIds(
      selected ? new Set(filteredRows.map((row) => row.clientId)) : new Set(),
    );
  };

  const handleApplyFlagsToCrew = () => {
    const workerIds = getActiveCrewWorkerIds();
    if (!workerIds) {
      toast({ variant: "error", title: "Select a crew tab first" });
      return;
    }

    const day = new Date(`${date}T12:00:00`).getDay();
    const isWeekend = day === 0 || day === 6;

    setRows((prev) =>
      prev.map((row) =>
        workerIds.has(row.workerId)
          ? {
              ...row,
              flags: {
                ...row.flags,
                isWeekend: isWeekend || row.flags.isWeekend,
              },
            }
          : row,
      ),
    );
    toast({
      variant: "success",
      title: "Applied flags to crew",
      description: isWeekend ? "Weekend flag set from selected date" : "No automatic flags for this date",
    });
  };

  const handleCopyYesterdayRow = (clientId: string) => {
    const row = rows.find((r) => r.clientId === clientId);
    const yesterday = yesterdayRows.find((y) => y.workerId === row?.workerId);
    if (!row || !yesterday) return;
    setRows((prev) =>
      prev.map((r) =>
        r.clientId === clientId
          ? {
              ...r,
              hours: yesterday.hours,
              jobCode: yesterday.jobCode ?? "",
              costCode: yesterday.costCode ?? "",
              flags: yesterday.flags,
              equipment: yesterday.equipment,
            }
          : r,
      ),
    );
  };

  if (!canRead) {
    return (
      <PageHeader
        title="Field Entry"
        description="You do not have permission to enter labor hours."
      />
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Field Entry"
        description="Log labor hours, equipment, and flags — built for foremen in the field."
        badge={
          dayMeta?.isLocked ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
              <HardHat className="h-3.5 w-3.5" />
              Locked
            </span>
          ) : undefined
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {canWrite && !dayMeta?.isLocked ? (
              <Button variant="outline" onClick={() => void handleSaveDraft()} disabled={saving}>
                <Save className="mr-1.5 h-4 w-4" />
                {saving ? "Saving…" : "Save draft"}
              </Button>
            ) : null}
            {canSubmit && !dayMeta?.isLocked ? (
              <Button onClick={() => void handleSubmit()} disabled={submitting}>
                <Send className="mr-1.5 h-4 w-4" />
                {submitting ? "Submitting…" : "Submit day"}
              </Button>
            ) : null}
          </div>
        }
      />

      {dayMeta?.isLocked ? (
        <InfoBanner icon={HardHat} variant="warning" title="Read-only board">
          {MICROCOPY.lockedPayPeriod}
        </InfoBanner>
      ) : null}

      {dayMeta?.geofence?.enabled ? (
        <InfoBanner icon={MapPin} variant="info" title="Geofencing active">
          {MICROCOPY.geofencePrivacy}
        </InfoBanner>
      ) : null}

      {/* Mobile field summary — sticky totals for foremen */}
      {siteId && options && !loading ? (
        <div className="sticky top-0 z-20 grid grid-cols-3 gap-2 rounded-xl border-2 border-navy-blueprint/20 bg-navy-deep p-3 text-white shadow-md lg:hidden">
          <div>
            <p className="text-[10px] uppercase text-white/60">Workers</p>
            <p className="text-lg font-bold tabular-nums">{summary.workerCount}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-white/60">Hours</p>
            <p className="text-lg font-bold tabular-nums">{formatHours(summary.totalHours)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-white/60">Issues</p>
            <p className={cn("text-lg font-bold tabular-nums", summary.exceptionCount > 0 && "text-amber-300")}>
              {summary.exceptionCount}
            </p>
          </div>
        </div>
      ) : null}

      <div className="rounded-xl border-2 border-navy-blueprint/15 bg-card-white p-4 shadow-[var(--shadow-card)]">
        <div className="grid gap-3 lg:grid-cols-4">
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Site
            </label>
            <select
              value={siteId}
              onChange={(e) => { setSiteId(e.target.value); }}
              disabled={sitesLoading || selectableSites.length === 0}
              className="flex h-11 w-full rounded-md border border-input px-3 text-sm font-medium disabled:opacity-60"
            >
              <option value="">
                {sitesLoading
                  ? "Loading sites…"
                  : selectableSites.length === 0
                    ? "No sites available"
                    : "Select site…"}
              </option>
              {selectableSites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.code} — {site.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Date
            </label>
            <div className="relative">
              <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="date"
                value={date}
                onChange={(e) => { setDate(e.target.value); }}
                className="h-11 pl-9 text-base font-medium"
              />
            </div>
          </div>
          <div className="space-y-1 lg:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Search worker
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => { setSearch(e.target.value); }}
                placeholder="Employee ID, name, job code…"
                className="h-11 pl-9"
              />
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 border-t pt-4">
          <div className="flex max-w-full gap-1 overflow-x-auto rounded-lg bg-bg-light p-1">
            <button
              type="button"
              onClick={() => { setActiveCrew("all"); }}
              className={`rounded-md px-3 py-1.5 text-sm font-semibold ${
                activeCrew === "all"
                  ? "bg-navy-deep text-white"
                  : "text-navy-deep hover:bg-white"
              }`}
            >
              All crew
            </button>
            {options?.crews.map((crew) => (
              <button
                key={crew.id}
                type="button"
                onClick={() => { setActiveCrew(crew.id); }}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold ${
                  activeCrew === crew.id
                    ? "bg-navy-deep text-white"
                    : "text-navy-deep hover:bg-white"
                }`}
              >
                {crew.name}
              </button>
            ))}
          </div>

          <div className="ml-auto flex flex-wrap gap-2">
            {canWrite && !dayMeta?.isLocked ? (
              <>
                <Button variant="outline" size="sm" onClick={() => void handleCopyPreviousDay()}>
                  <ClipboardCopy className="mr-1.5 h-4 w-4" />
                  Copy previous day
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setWorkerModalOpen(true); }}>
                  <Plus className="mr-1.5 h-4 w-4" />
                  Add worker
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setCrewModalOpen(true); }}>
                  <Users className="mr-1.5 h-4 w-4" />
                  Add crew
                </Button>
                <Button variant="outline" size="sm" onClick={handleApplyFlagsToCrew}>
                  Apply flags to crew
                </Button>
                <Button variant="outline" size="sm" onClick={handleApplyEquipmentToCrew}>
                  <Wrench className="mr-1.5 h-4 w-4" />
                  Apply equipment to crew
                </Button>
                <Button variant="outline" size="sm" onClick={handleApplyEquipmentToSelected}>
                  Apply equipment to selected
                </Button>
                <Button variant="outline" size="sm" onClick={handleCopyYesterdayEquipment}>
                  Copy yesterday&apos;s equipment
                </Button>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {!siteId ? (
        <EmptyState
          icon={selectableSites.length === 0 && !sitesLoading ? Building2 : MapPin}
          title={
            selectableSites.length === 0 && !sitesLoading
              ? BETA_EMPTY_STATES.sites.title
              : "Select a job site"
          }
          description={
            selectableSites.length === 0 && !sitesLoading
              ? BETA_EMPTY_STATES.sites.description
              : "Choose a job site to open today's labor board and start field entry."
          }
          actionLabel={
            selectableSites.length === 0 && !sitesLoading
              ? BETA_EMPTY_STATES.sites.actionLabel
              : undefined
          }
          onAction={
            selectableSites.length === 0 && !sitesLoading
              ? () => { navigate("/sites/new"); }
              : undefined
          }
        />
      ) : loading || !options ? (
        <ListTableSkeleton rows={10} cols={8} />
      ) : (
        <div className="grid gap-4 xl:grid-cols-[1fr_300px]">
          <DailyEntryTable
            rows={filteredRows}
            options={options}
            date={date}
            canEdit={canWrite && !dayMeta?.isLocked}
            canViewPayroll={canViewPayroll}
            canViewEquipmentCosts={canViewEquipmentCosts}
            selectedRowIds={selectedRowIds}
            onToggleRowSelected={handleToggleRowSelected}
            onToggleAllRowsSelected={handleToggleAllRowsSelected}
            onChangeRow={(clientId, patch) =>
              { setRows((prev) =>
                prev.map((row) => (row.clientId === clientId ? { ...row, ...patch } : row)),
              ); }
            }
            onRemoveRow={(clientId) =>
              { setRows((prev) => prev.filter((row) => row.clientId !== clientId)); }
            }
            onCopyYesterday={handleCopyYesterdayRow}
            yesterdayRows={yesterdayRows}
            onStartFieldEntry={
              canWrite && !dayMeta?.isLocked ? () => { setWorkerModalOpen(true); } : undefined
            }
          />
          <DailyEntrySummaryPanel
            summary={summary}
            canViewPayroll={canViewPayroll}
            isLocked={dayMeta?.isLocked ?? false}
            payPeriodLabel={dayMeta?.payPeriodLabel ?? null}
          />
        </div>
      )}
      {options ? (
        <ApplyEquipmentModal
          open={applyEquipmentModalOpen}
          onClose={() => { setApplyEquipmentModalOpen(false); }}
          options={options}
          target={applyEquipmentTarget}
          targetLabel={options.crews.find((crew) => crew.id === activeCrew)?.name ?? "crew"}
          workerCount={
            applyEquipmentTarget === "crew"
              ? filteredRows.filter((row) => getActiveCrewWorkerIds()?.has(row.workerId)).length
              : selectedRowIds.size
          }
          canViewEquipmentCosts={canViewEquipmentCosts}
          onApply={handleApplyEquipment}
        />
      ) : null}
      {options ? (
        <AddCrewModal
          open={crewModalOpen}
          onClose={() => { setCrewModalOpen(false); }}
          options={options}
          existingWorkerIds={rows.map((r) => r.workerId)}
          onApply={applyCrewToBoard}
        />
      ) : null}
      {siteId ? (
        <AddWorkerModal
          open={workerModalOpen}
          siteId={siteId}
          excludeWorkerIds={rows.map((r) => r.workerId)}
          onClose={() => { setWorkerModalOpen(false); }}
          onSelect={handleAddWorker}
        />
      ) : null}

      <GeofenceSubmitModal
        open={geofenceModalOpen}
        title={
          pendingGeolocation
            ? "Outside site geofence"
            : "GPS unavailable"
        }
        description={
          pendingGeolocation
            ? "Your location appears outside the site boundary. You can still submit in beta — add a note if needed."
            : "We could not read your location. You can still submit in beta — add a note explaining why."
        }
        overrideReason={geofenceOverrideReason}
        onOverrideReasonChange={setGeofenceOverrideReason}
        onCancel={() => { setGeofenceModalOpen(false); }}
        onConfirm={() => void handleSubmit(geofenceOverrideReason, pendingGeolocation)}
        submitting={submitting}
      />
    </div>
  );
}
